import { Logo } from "@/components/Logo";
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";

export default function Auth() {
  const [mode, setMode] = useState("login"); // login | signup | forgot
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let u;
      if (mode === "login") u = await login(email, password);
      else u = await signup(name, email, password);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      if (!u.profile_complete) navigate("/onboarding");
      else navigate(location.state?.from?.pathname || "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const forgotSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, base_url: window.location.origin });
      setSent(true);
    } catch (err) {
      // Endpoint always succeeds generically; only network errors land here.
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (m) => { setMode(m); setSent(false); setPassword(""); };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <div className="rd-hero-sheen absolute inset-0 h-[40vh] pointer-events-none" />
      <div className="relative flex-1 flex flex-col justify-center px-6 max-w-[440px] mx-auto w-full">
        <div className="mb-8">
          <Logo className="h-20 w-20 rounded-2xl rd-glow mb-4" />
          <h1 className="font-display text-4xl font-bold leading-tight">
            REAL DEAL <span className="rd-lime">FITNESS</span>
          </h1>
          <p className="text-muted-foreground mt-2">Premium coaching. Real results. No shortcuts.</p>
        </div>

        {mode === "forgot" ? (
          sent ? (
            <div data-testid="forgot-sent" className="rounded-2xl bg-card border border-border p-6 text-center">
              <div className="h-12 w-12 rounded-2xl bg-[#ccff0c]/15 flex items-center justify-center mx-auto mb-3">
                <MailCheck className="h-6 w-6 text-[#ccff0c]" />
              </div>
              <h2 className="font-display text-xl font-bold">Check your email</h2>
              <p className="text-muted-foreground text-sm mt-2">If an account exists for <span className="text-foreground">{email}</span>, we've sent a link to reset your password. The link expires in 1 hour.</p>
              <Button data-testid="forgot-back-login" onClick={() => switchMode("login")} className="w-full mt-5 h-11 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">
                Back to log in
              </Button>
            </div>
          ) : (
            <form onSubmit={forgotSubmit} className="space-y-4" data-testid="forgot-form">
              <div>
                <h2 className="font-display text-xl font-bold">Reset your password</h2>
                <p className="text-muted-foreground text-sm mt-1">Enter your email and we'll send you a reset link.</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fp-email">Email</Label>
                <Input data-testid="forgot-email" id="fp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" className="bg-card border-border h-11" />
              </div>
              <Button data-testid="forgot-submit" type="submit" disabled={loading} className="w-full h-12 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] rd-glow font-semibold text-base">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send reset link"}
              </Button>
              <button type="button" data-testid="forgot-cancel" onClick={() => switchMode("login")} className="w-full flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-4 w-4" /> Back to log in
              </button>
            </form>
          )
        ) : (
          <>
            <form onSubmit={submit} className="space-y-4">
              {mode === "signup" && (
                <div className="space-y-1.5">
                  <Label htmlFor="name">Name</Label>
                  <Input data-testid="auth-name" id="name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Your name" className="bg-card border-border h-11" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input data-testid="auth-email" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" className="bg-card border-border h-11" />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  {mode === "login" && (
                    <button type="button" data-testid="forgot-link" onClick={() => switchMode("forgot")} className="text-xs text-[#ccff0c] hover:opacity-80">
                      Forgot password?
                    </button>
                  )}
                </div>
                <Input data-testid="auth-password" id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" className="bg-card border-border h-11" />
              </div>
              <Button data-testid="auth-submit" type="submit" disabled={loading} className="w-full h-12 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] rd-glow font-semibold text-base">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : mode === "login" ? "Log In" : "Start 7-Day Free Trial"}
              </Button>
            </form>

            <button
              data-testid="auth-toggle"
              onClick={() => switchMode(mode === "login" ? "signup" : "login")}
              className="mt-6 text-center text-sm text-muted-foreground hover:text-foreground"
            >
              {mode === "login" ? "New here? Create an account" : "Already a member? Log in"}
            </button>
            <p className="mt-4 text-center text-[11px] text-muted-foreground">
              By continuing you agree to our{" "}
              <button type="button" data-testid="auth-privacy-link" onClick={() => navigate("/privacy")} className="rd-lime hover:opacity-80 underline underline-offset-2">Privacy Policy</button>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
